import React, { forwardRef, SelectHTMLAttributes, useState, useRef, useEffect, useMemo } from 'react';
import { IconMaterial } from '../../iconMaterial/IconMaterial';
import { useTranslation } from 'react-i18next';

export interface SelectInputProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size' | 'multiple' | 'value' | 'onChange'> {
    label?: string;
    labelClass?: string;
    placeholder?: string;
    required?: boolean;
    options: Array<{
        id: string | number;
        slug?: string;
        label?: string;
        name?: string;
        name_ru?: string;
    }>;
    helpText?: string;
    error?: string;
    success?: boolean;
    important?: boolean;
    size?: 'sm' | 'md' | 'lg' | 'default';
    variant?: 'default' | 'filled' | 'outlined';
    className?: string;
    multiSelect?: boolean;
    searchable?: boolean;
    searchPlaceholder?: string;
    noResultsText?: string;
    value?: number | string | string[] | null;
    onChange?: (value: string | string[] | number | null) => void;
    maxSelections?: number;
    clearable?: boolean;
    onClear?: () => void;
}

const SelectInput = forwardRef<HTMLSelectElement, SelectInputProps>(({
    label,
    labelClass = 'text-primary-dark',
    required = false,
    important = false,
    placeholder,
    options,
    helpText,
    error,
    success,
    size = 'default',
    variant = 'default',
    className = '',
    disabled = false,
    multiSelect = false,
    searchable = false,
    clearable = false,
    onClear,
    searchPlaceholder,
    noResultsText,
    value,
    onChange,
    maxSelections,
    ...props
}, ref) => {
    const { t } = useTranslation()
    const [isOpen, setIsOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [selectedValues, setSelectedValues] = useState<string[]>(
        multiSelect
            ? (Array.isArray(value) ? value : [])
            : (value ? [value as string] : [])
    );

    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

    useEffect(() => {
        if (multiSelect) {
            setSelectedValues(Array.isArray(value) ? value : []);
        } else {
            setSelectedValues(value ? [value as string] : []);
        }
    }, [value, multiSelect]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchQuery('');
                setFocusedIndex(-1);
                setIsFocused(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const filteredOptions = useMemo(() => {
        if (!searchQuery.trim()) return options;

        return options.filter(option => {
            const searchableText = (option.label || option.name || '').toLowerCase();
            return searchableText.includes(searchQuery.toLowerCase());
        });
    }, [options, searchQuery]);

    const sizeClasses = {
        sm: 'px-3 py-2 text-xs w-[200px] h-[44px]',
        md: 'px-4 py-2 text-md w-[244px] h-[44px]',
        lg: 'px-4 py-3 text-sm w-[520px] h-auto',
        default: "w-full px-4 py-2 text-md h-[44px]"
    };

    const getSelectClasses = () => {
        const baseClasses = `
            rounded-lg inline-flex justify-start text-sm items-center gap-2 transition-all duration-200 min-h-[44px] h-auto
            ${sizeClasses[size]}
        `;

        const stateClasses = error
            ? 'outline outline-1 outline-offset-[-1px] outline-red-500 '
            : success
                ? 'outline outline-1 outline-offset-[-1px] outline-green-500 '
                : (isFocused || isOpen)
                    ? 'outline outline-2 outline-offset-[-1px] outline-blue-500 bg-white'
                    : 'outline outline-1 outline-offset-[-1px] outline-gray-300 bg-white';

        const disabledClasses = disabled
            ? 'opacity-50 cursor-not-allowed bg-gray-100'
            : 'cursor-pointer';

        return `${baseClasses} ${stateClasses} ${disabledClasses}`;
    };

    const getLabelClasses = () => {
        const baseClasses = 'font-medium leading-snug';
        const sizeClasses = {
            sm: 'text-xs',
            md: 'text-sm',
            lg: 'text-base',
            default: 'text-sm'
        };
        return `${baseClasses} ${sizeClasses[size]} ${labelClass}`;
    };

    const getHelpTextClasses = () => {
        const baseClasses = 'leading-snug';
        const sizeClasses = {
            sm: 'text-xs',
            md: 'text-sm',
            lg: 'text-base',
            default: 'text-sm'
        };

        if (error) {
            return `${baseClasses} text-sm text-red-600`;
        }
        if (success) {
            return `${baseClasses} ${sizeClasses[size]} text-green-600`;
        }
        return `${baseClasses} ${sizeClasses[size]} text-secondary`;
    };

    const handleOptionClick = (optionId: string) => {
        if (disabled) return;

        if (multiSelect) {
            let newSelectedValues: string[];

            if (selectedValues.includes(optionId)) {
                newSelectedValues = selectedValues.filter(id => id !== optionId);
            } else {
                if (maxSelections && selectedValues.length >= maxSelections) {
                    return;
                }
                newSelectedValues = [...selectedValues, optionId];
            }

            setSelectedValues(newSelectedValues);
            onChange?.(newSelectedValues);
        } else {
            setSelectedValues([optionId]);
            onChange?.(optionId);
            setIsOpen(false);
            setSearchQuery('');
            setFocusedIndex(-1);
            setIsFocused(false);
        }
    };

    const removeSelection = (optionId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (disabled) return;

        const newSelectedValues = selectedValues.filter(id => id !== optionId);
        setSelectedValues(newSelectedValues);
        onChange?.(multiSelect ? newSelectedValues : '');
    };

    const getSelectedOptions = () => {
        return options.filter(option => selectedValues.includes(option.id.toString()));
    };

    const isSelected = (optionId: string) => {
        return selectedValues.includes(optionId);
    };

    const handleDropdownToggle = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
            setIsFocused(!isOpen);
            if (!isOpen) {
                setSearchQuery('');
                setFocusedIndex(-1);
            }
        }
    };

    const handleClear = () => {
        if (onClear) {
            onClear();
        }
    };

    return (
        <div className={`w-full inline-flex flex-col justify-start items-start gap-3 ${className}`} ref={dropdownRef}>
            {label && (
                <div className="w-full inline-flex justify-start items-center gap-1">
                    <div className={getLabelClasses()}>
                        {label}
                        {important && <span className="text-red-500 ml-1">*</span>}
                    </div>
                </div>
            )}

            <div className="relative w-full">
                <button
                    type="button"
                    onClick={handleDropdownToggle}
                    className={getSelectClasses()}
                    disabled={disabled}
                >
                    <div className="flex-1 flex items-center gap-2 min-h-0">
                        {selectedValues.length > 0 ? (
                            <div className="flex flex-wrap gap-1 flex-1 items-center">
                                {(multiSelect && selectedValues.length > 1) ? (
                                    getSelectedOptions().map((option) => (
                                        <div
                                            key={option.id}
                                            className="inline-flex items-center h-6 gap-1 bg-background-light-gray text-primary-dark px-2 py-1 rounded-[100px] text-xs font-medium"
                                        >
                                            <span>{option.label || option.name}</span>
                                            <button
                                                type="button"
                                                onClick={(e) => removeSelection(option.id.toString(), e)}
                                            >
                                                <IconMaterial
                                                    className='mt-1'
                                                    filled
                                                    icon='close'
                                                    size={16}
                                                    iconColor="var(--tokens-text-secondary-text)"
                                                />
                                            </button>
                                        </div>
                                    ))
                                ) : (
                                    <span className="text-secondary-strong leading-snug">
                                        {getSelectedOptions()[0]?.label || getSelectedOptions()[0]?.name}
                                    </span>
                                )}
                            </div>
                        ) : (
                            <span className="text-secondary leading-snug">
                                {placeholder || t("select")}
                            </span>
                        )}
                    </div>

                    {(getSelectedOptions()[0] && clearable && !disabled) && (
                        <div
                            className="text-secondary flex-shrink-0 flex items-center"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClear();
                            }}
                        >
                            <IconMaterial
                                className="cursor-pointer pointer-events-auto"
                                icon="close"
                                filled
                                size={18}
                            />
                        </div>
                    )}

                    <div className="text-secondary flex-shrink-0 flex items-center">
                        <IconMaterial
                            className={`transition-transform duration-200 ${isOpen && 'rotate-180'}`}
                            icon='expand_more'
                            filled
                            size={20}
                        />
                    </div>
                </button>

                {isOpen && (
                    <div className="absolute    top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-64 overflow-hidden">
                        {searchable && (
                            <div className="p-3 border-b border-gray-200 h-16">
                                <div className="relative">
                                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary">
                                        <IconMaterial
                                            className='mt-1'
                                            filled
                                            icon='search'
                                            size={12}
                                            iconColor="var(--tokens-text-secondary-text)"
                                        />
                                    </div>
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        placeholder={searchPlaceholder || t("search-options")}
                                        value={searchQuery}
                                        onChange={(e) => {
                                            setSearchQuery(e.target.value);
                                            setFocusedIndex(-1);
                                        }}
                                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-secondary-strong placeholder:text-secondary"
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </div>
                            </div>
                        )}

                        <div className="max-h-48 overflow-y-auto p-2 flex flex-col gap-1">
                            {filteredOptions.length > 0 ? (
                                filteredOptions.map((option, index) => {
                                    const isOptionSelected = isSelected(option.id.toString());
                                    const isDisabled = !isOptionSelected && maxSelections && selectedValues.length >= maxSelections;

                                    return (
                                        <button
                                            key={option.id}
                                            ref={(el) => (optionRefs.current[index] = el)}
                                            type="button"
                                            onClick={() => handleOptionClick(option.id.toString())}
                                            className={`w-full group flex items-center justify-between rounded-lg px-4 py-3 flex items-center gap-3 transition-colors text-left ${isOptionSelected
                                                ? 'bg-background-light-gray'
                                                : 'hover:bg-background-light-gray'
                                                } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                            <span className={`text-sm w-full leading-snug truncate group-hover:text-primeblue ${isOptionSelected && "text-primeblue"}`}>
                                                {option.label || option.name || option.name_ru}
                                            </span>
                                            {isOptionSelected && (
                                                <div className='rounded-full mr-1 bg-primary text-white flex items-center justifiy-center'>
                                                    <IconMaterial
                                                        filled
                                                        icon={'check'}
                                                        className="cursor-pointer"
                                                        size={16}
                                                        iconColor={"var(--tokens-text-secondary-text)"}
                                                    />
                                                </div>
                                            )}
                                        </button>
                                    );
                                })
                            ) : (
                                <div className="px-4 py-8 text-center text-secondary text-sm">
                                    {noResultsText || t("noOptionsFound")}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {(helpText || error) && (
                <div className={getHelpTextClasses()}>
                    {error || helpText}
                </div>
            )}
        </div>
    );
});

SelectInput.displayName = 'SelectInput';

export default SelectInput;